import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import HubupPremiumQr from '../../components/hubup/HubupPremiumQr';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Button from '../../components/ui/Button';
import { useUserAccess } from '../../context/UserAccessContext';
import { useHubupCommunities } from '../../context/HubupCommunitiesContext';
import { buildHubupGroupInviteUrl, fetchHubupGroupNotificationsHourly, fetchHubupGroupStats } from '../../api/hubup';

function formatStatInt(n) {
  return Number(n ?? 0).toLocaleString('fr-FR');
}

function formatStatAvg(n) {
  const x = Number(n ?? 0);
  if (Number.isNaN(x)) return '0';
  return x.toLocaleString('fr-FR', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

function HubupStatCard({ label, value }) {
  return (
    <div className="bg-card rounded-lg border border-border p-4 shadow-elevation-1 flex flex-col min-h-[4.5rem]">
      <p className="text-xs font-medium text-muted-foreground leading-tight">{label}</p>
      <p className="text-xl sm:text-2xl font-semibold text-foreground tabular-nums mt-2">{value}</p>
    </div>
  );
}

const PARIS_TZ = 'Europe/Paris';

function parisDayKeyFromInstant(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-CA', { timeZone: PARIS_TZ });
  } catch {
    return '';
  }
}

function enumerateParisCalendarDays(startIso, endIso) {
  const keys = [];
  const seen = new Set();
  const startT = new Date(startIso).getTime();
  const endT = new Date(endIso).getTime();
  if (!Number.isFinite(startT) || !Number.isFinite(endT) || startT > endT) {
    return keys;
  }
  for (let t = startT; t <= endT; t += 60 * 60 * 1000) {
    const k = new Date(t).toLocaleDateString('fr-CA', { timeZone: PARIS_TZ });
    if (k && !seen.has(k)) {
      seen.add(k);
      keys.push(k);
    }
  }
  return keys.sort();
}

function axisLabelFromParisDayKey(dayKey) {
  const parts = dayKey.split('-').map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return dayKey;
  const [y, m, d] = parts;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

function tooltipTitleFromParisDayKey(dayKey) {
  const parts = dayKey.split('-').map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return dayKey;
  const [y, m, d] = parts;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatRangeEndLabel(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      timeZone: PARIS_TZ,
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const RANGE_OPTIONS = [
  { value: 7, label: '7 jours' },
  { value: 14, label: '14 jours' },
  { value: 30, label: '30 jours' },
];

const mainLineProps = {
  type: 'monotone',
  strokeWidth: 2.5,
  dot: false,
  activeDot: { r: 6 },
};

const TOOLTIP_DETAIL_ROWS = [
  { key: 'pushSentSum', label: 'Push réussis 1er envoi' },
  { key: 'pushFailedSum', label: 'Push échoués' },
  { key: 'lastResendPushSentSum', label: 'Renvois push' },
  { key: 'readedSum', label: 'Lus compteur' },
];

function NotificationsDailyTooltip({ active, payload }) {
  if (active && payload?.length) {
    const row = payload[0]?.payload;
    if (!row) return null;
    const titles = Array.isArray(row.titles) ? row.titles : [];
    const title = row.labelFull || row.label || '';
    const count = Number(row.notifications) || 0;
    return (
      <div className="bg-popover p-3 md:p-4 rounded-lg shadow-elevation-3 border border-border max-w-sm">
        <p className="text-xs md:text-sm font-medium text-popover-foreground mb-3">{title}</p>
        <div className="flex items-center justify-between gap-4 mb-3 pb-3 border-b border-border">
          <span className="text-sm font-medium text-foreground">Nombre de notifications</span>
          <span
            className="text-base font-semibold tabular-nums"
            style={{ color: 'var(--color-primary)' }}
          >
            {count}
          </span>
        </div>
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-2">
          Push et lectures
        </p>
        <div className="space-y-1.5 mb-3">
          {TOOLTIP_DETAIL_ROWS.map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between gap-3 text-xs">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-semibold tabular-nums text-popover-foreground">
                {Number(row[key]) || 0}
              </span>
            </div>
          ))}
        </div>
        {titles.length > 0 ? (
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground mb-1.5">
              Titres des notifications
            </p>
            <ul className="text-xs text-popover-foreground space-y-1 max-h-36 overflow-y-auto pr-1">
              {titles.map((t, i) => (
                <li key={i} className="leading-snug flex gap-1.5">
                  <span className="text-muted-foreground shrink-0 select-none" aria-hidden>
                    •
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );
  }
  return null;
}

function buildDailyChartData(stats) {
  if (!stats?.start || !stats?.end) return [];
  const dayKeys = enumerateParisCalendarDays(stats.start, stats.end);
  if (dayKeys.length === 0) return [];

  const byDay = new Map();
  for (const h of stats.hours || []) {
    const k = parisDayKeyFromInstant(h.hourStart);
    if (!k) continue;
    if (!byDay.has(k)) {
      byDay.set(k, {
        notifications: 0,
        pushSentSum: 0,
        pushFailedSum: 0,
        lastResendPushSentSum: 0,
        readedSum: 0,
        titles: [],
      });
    }
    const row = byDay.get(k);
    row.notifications += Number(h.notifications) || 0;
    row.pushSentSum += Number(h.pushSentSum) || 0;
    row.pushFailedSum += Number(h.pushFailedSum) || 0;
    row.lastResendPushSentSum += Number(h.lastResendPushSentSum) || 0;
    row.readedSum += Number(h.readedSum) || 0;
    for (const t of Array.isArray(h.titles) ? h.titles : []) {
      const s = String(t ?? '').trim();
      if (s) row.titles.push(s);
    }
  }

  return dayKeys.map((dayKey) => {
    const agg = byDay.get(dayKey);
    const titles = agg ? [...new Set(agg.titles)].sort((a, b) => a.localeCompare(b, 'fr')) : [];
    return {
      dayKey,
      label: axisLabelFromParisDayKey(dayKey),
      labelFull: tooltipTitleFromParisDayKey(dayKey),
      notifications: agg?.notifications ?? 0,
      pushSentSum: agg?.pushSentSum ?? 0,
      pushFailedSum: agg?.pushFailedSum ?? 0,
      lastResendPushSentSum: agg?.lastResendPushSentSum ?? 0,
      readedSum: agg?.readedSum ?? 0,
      titles,
    };
  });
}

const HubupDashboard = () => {
  const { groupId } = useParams();
  const { user } = useUserAccess();
  const { communities } = useHubupCommunities();
  const isAdmin = user?.permission === 'admin';
  const currentCommunity = useMemo(
    () => communities.find((c) => c.id === groupId),
    [communities, groupId],
  );
  const notifRemaining = useMemo(() => {
    if (isAdmin) return null;
    return Math.max(0, Number(currentCommunity?.notificationLeft) || 0);
  }, [isAdmin, currentCommunity?.notificationLeft]);
  const [rangeDays, setRangeDays] = useState(7);
  const [stats, setStats] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copyHint, setCopyHint] = useState('');
  const copyHintTimerRef = useRef(null);

  const inviteCode = currentCommunity?.inviteCode
    ? String(currentCommunity.inviteCode).trim()
    : '';
  const inviteUrl = inviteCode ? buildHubupGroupInviteUrl(inviteCode) : '';

  useEffect(() => {
    if (!copyHint) return undefined;
    if (copyHintTimerRef.current) clearTimeout(copyHintTimerRef.current);
    copyHintTimerRef.current = setTimeout(() => {
      setCopyHint('');
      copyHintTimerRef.current = null;
    }, 2200);
    return () => {
      if (copyHintTimerRef.current) clearTimeout(copyHintTimerRef.current);
    };
  }, [copyHint]);

  const copyInviteLink = async () => {
    const url = buildHubupGroupInviteUrl(inviteCode);
    if (!url) {
      setCopyHint('Lien indisponible pour le moment.');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopyHint('Lien d’invitation copié.');
    } catch {
      setCopyHint('Impossible de copier le lien.');
    }
  };

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError(null);
    setDashboardStats(null);
    const end = new Date();
    const start = new Date(end.getTime() - rangeDays * 24 * 60 * 60 * 1000);
    try {
      const [hourlyOutcome, statsOutcome] = await Promise.allSettled([
        fetchHubupGroupNotificationsHourly(groupId, {
          start: start.toISOString(),
          end: end.toISOString(),
        }),
        fetchHubupGroupStats(groupId),
      ]);

      if (hourlyOutcome.status === 'fulfilled') {
        const d = hourlyOutcome.value?.data;
        if (!d || !Array.isArray(d.hours)) {
          throw new Error('Réponse courbe invalide');
        }
        setStats(d);
      } else {
        setStats(null);
        setError('Impossible de charger les statistiques des notifications.');
      }

      if (statsOutcome.status === 'fulfilled') {
        const raw = statsOutcome.value?.data;
        if (
          raw &&
          typeof raw === 'object' &&
          (raw.subscribers != null ||
            raw.notifications != null ||
            raw.topics != null)
        ) {
          setDashboardStats({
            subscriberCount: Number(raw.subscribers ?? 0),
            notificationCount: Number(raw.notifications ?? 0),
            topicCount: Number(raw.topics ?? 0),
            avgReadReceiptsPerNotification: Number(
              raw.avgReadsPerNotification ?? 0,
            ),
            profileViewCount: Number(raw.profileViewCount ?? 0),
          });
        } else {
          setDashboardStats(null);
        }
      } else {
        setDashboardStats(null);
      }
    } catch (e) {
      setError('Impossible de charger les statistiques des notifications.');
      setStats(null);
      setDashboardStats(null);
    } finally {
      setLoading(false);
    }
  }, [groupId, rangeDays]);

  useEffect(() => {
    load();
  }, [load]);

  const chartData = useMemo(() => buildDailyChartData(stats), [stats]);

  const periodLabel = stats?.start && stats?.end
    ? `${formatRangeEndLabel(stats.start)} — ${formatRangeEndLabel(stats.end)}`
    : '';

  const xAxisBottom = chartData.length > 14 ? 72 : 52;

  return (
    <>
      <Helmet>
        <title>Tableau de bord — HubUP</title>
      </Helmet>
      <main className="main-content">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
            Tableau de bord
          </h1>
          <p className="text-sm text-muted-foreground">
            Vue d&apos;ensemble de votre espace HubUP pour cette communauté.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 mb-8">
          {loading && !dashboardStats && notifRemaining === null ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-24 bg-muted animate-pulse rounded-lg border border-border"
                aria-hidden
              />
            ))
          ) : dashboardStats || notifRemaining !== null ? (
            <>
              {notifRemaining !== null ? (
                <HubupStatCard
                  label="Notifications restantes"
                  value={formatStatInt(notifRemaining)}
                />
              ) : null}
              {dashboardStats ? (
                <>
              <HubupStatCard
                label="Abonnés"
                value={formatStatInt(dashboardStats.subscriberCount)}
              />
              <HubupStatCard
                label="Notifications"
                value={formatStatInt(dashboardStats.notificationCount)}
              />
              <HubupStatCard
                label="Rubriques"
                value={formatStatInt(dashboardStats.topicCount)}
              />
              <HubupStatCard
                label="Lus en moyenne / notification"
                value={formatStatAvg(dashboardStats.avgReadReceiptsPerNotification)}
              />
              <HubupStatCard
                label="Vues du profil public"
                value={formatStatInt(dashboardStats.profileViewCount)}
              />
                </>
              ) : null}
            </>
          ) : (
            <p className="col-span-full text-xs text-muted-foreground py-2">
              Synthèse communauté indisponible.
            </p>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-6 items-stretch">
          <div className="w-full lg:flex-1 lg:min-w-0">
            <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-elevation-2 h-full">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                <div className="min-w-0">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">
                    Notifications par jour
                  </h2>
                  {periodLabel ? (
                    <p className="text-xs text-muted-foreground mt-2" title={periodLabel}>
                      {periodLabel}
                    </p>
                  ) : null}
                </div>
                <label className="text-sm font-medium text-foreground shrink-0 sm:w-44">
                  Période
                  <select
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={rangeDays}
                    onChange={(ev) => setRangeDays(Number(ev.target.value))}
                  >
                    {RANGE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {loading ? (
                <div
                  className="w-full h-64 md:h-80 lg:h-96 bg-muted animate-pulse rounded-lg"
                  aria-hidden
                />
              ) : error ? (
                <p className="text-sm text-error py-12 text-center">{error}</p>
              ) : chartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-16 text-center">
                  Période invalide ou vide.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  <div
                    className="w-full h-64 md:h-80 lg:h-96"
                    aria-label="Graphique des notifications par jour"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: 0, bottom: xAxisBottom }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                        <XAxis
                          dataKey="label"
                          stroke="var(--color-text-secondary)"
                          style={{ fontSize: '11px' }}
                          tick={{ fill: 'var(--color-text-secondary)' }}
                          tickLine={{ stroke: 'var(--color-text-secondary)' }}
                          axisLine={{ stroke: 'var(--color-border)' }}
                          interval={chartData.length > 18 ? Math.ceil(chartData.length / 18) : 0}
                          angle={chartData.length > 10 ? -35 : 0}
                          textAnchor={chartData.length > 10 ? 'end' : 'middle'}
                          height={xAxisBottom - 8}
                        />
                        <YAxis
                          stroke="var(--color-text-secondary)"
                          style={{ fontSize: '12px' }}
                          tick={{ fill: 'var(--color-text-secondary)' }}
                          allowDecimals={false}
                          width={44}
                          domain={[0, 'auto']}
                        />
                        <Tooltip content={<NotificationsDailyTooltip />} />
                        <Legend
                          wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                          iconType="plainline"
                        />
                        <Line
                          dataKey="notifications"
                          name="Nombre de notifications"
                          stroke="var(--color-primary)"
                          {...mainLineProps}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="w-full lg:w-[34%] lg:min-w-[280px] lg:max-w-md shrink-0">
            <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-elevation-2 h-full flex flex-col min-h-0">
              <div className="shrink-0">
                <h2 className="text-base font-semibold text-foreground mb-4">
                  Invitation
                </h2>
              </div>

              {!inviteCode ? (
                <p className="text-xs text-muted-foreground shrink-0">
                  Code d’invitation non disponible pour cette communauté.
                </p>
              ) : inviteUrl ? (
                <>
                  <div className="flex-1 flex flex-col items-center justify-center min-h-[12rem] py-4">
                    <div className="flex justify-center bg-background rounded-lg p-5 border border-border w-full">
                      <HubupPremiumQr
                        groupId={groupId}
                        inviteCode={inviteCode}
                        groupIconUrl={currentCommunity?.groupIconUrl}
                        faviconUrl={currentCommunity?.faviconUrl}
                        size={200}
                      />
                    </div>
                  </div>
                  <div className="shrink-0 pt-2 mt-auto space-y-2">
                    {copyHint ? (
                      <p className="text-[11px] text-muted-foreground">{copyHint}</p>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      iconName="Copy"
                      iconPosition="left"
                      onClick={() => void copyInviteLink()}
                    >
                      Copier le lien d’invitation
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground leading-relaxed shrink-0">
                  Lien d&apos;invitation indisponible pour le moment. Contactez
                  l&apos;administrateur si le problème persiste.
                </p>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default HubupDashboard;
